import { ApiError } from "@models/error";
import type {
  IMappingJobListResponse,
  IMappingJobViewModel,
} from "@models/mapping";
import { buildMappingJobViewModelFromStatus, HttpClient } from "@utils/index";

const BASE_URL = import.meta.env.VITE_MAPPING_API_BASE_URL;

if (!BASE_URL) {
  // The extraction UI works without mapping configured — it simply never
  // shows mapping progress. Warn rather than throw so the app still boots.
  console.warn(
    "VITE_MAPPING_API_BASE_URL is not set. Mapping status will not be shown " +
      "next to extraction results until it is configured in .env.",
  );
}

/**
 * Thin REST client for the xplatform mapping service. Used as the
 * initial-load / reconnect fallback for the live notification socket: jobs
 * are looked up by the extraction process_id.
 */
class MappingApi {
  private httpClient: HttpClient;
  private dispatchedProcesses = new Set<string>();
  private dispatchSubscribers = new Set<(processId: string) => void>();
  private retrySubscribers = new Set<
    (processId: string, staleJobId: string | null) => void
  >();

  constructor() {
    this.httpClient = new HttpClient(BASE_URL ?? "");
  }

  /** Notify other result views when an on-demand job was requested or found. */
  public markDispatched(processId: string): void {
    this.dispatchedProcesses.add(processId);
    for (const callback of this.dispatchSubscribers) callback(processId);
  }

  public wasDispatched(processId: string): boolean {
    return this.dispatchedProcesses.has(processId);
  }

  public onDispatch(callback: (processId: string) => void): () => void {
    this.dispatchSubscribers.add(callback);
    return () => this.dispatchSubscribers.delete(callback);
  }

  /**
   * Tell every status view of `processId` that a retry was requested, so they
   * all go back to waiting. `staleJobId` is the failed job being replaced; it
   * stays the newest job until mapping creates the retry, so views ignore it.
   */
  public markRetried(processId: string, staleJobId: string | null): void {
    for (const callback of this.retrySubscribers) callback(processId, staleJobId);
  }

  public onRetry(
    callback: (processId: string, staleJobId: string | null) => void,
  ): () => void {
    this.retrySubscribers.add(callback);
    return () => this.retrySubscribers.delete(callback);
  }

  /**
   * GET /jobs?process_id=... → the mapping jobs for an extraction process;
   * select the newest job, or `null` before one has been dispatched.
   */
  public async getMappingJobByProcess(
    processId: string,
  ): Promise<IMappingJobViewModel | null> {
    if (!BASE_URL) {
      return null;
    }
    const params = new URLSearchParams({ process_id: processId });
    try {
      const response =
        await this.httpClient.makeAuthenticatedRequest<IMappingJobListResponse>(
          `/jobs?${params.toString()}`,
          { method: "GET" },
        );
      const job = response?.jobs[0];
      if (!job) {
        return null;
      }
      return buildMappingJobViewModelFromStatus({ ...job, process_id: processId });
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 404) {
        return null;
      }
      throw error;
    }
  }
}

export const mappingApi = new MappingApi();
